/**
 * CompareTable — full feature comparison table for the pricing section.
 *
 * Spec: /app/memory/broadcastflow_compare_plans_prompt.md (Feb 2026).
 *
 * Renders inline below the 5 existing pricing cards on `<section id="pricing">`
 * in Landing.jsx. The parent controls the open state and the fade-in wrapper
 * so the toggle button and the table can be positioned together.
 *
 * Style: follows the spec's inline hex colors verbatim (dark, near-mono
 * pallette that matches the rest of Landing.jsx). Uses `table-layout: fixed`
 * for equal columns and horizontal scroll on <768px.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "@/lib/currency";

const PLAN_COLS = [
  { id: "free",         name: "Free",         priceUsd: 0,   cta: "Start free →",     ctaVariant: "outline" },
  { id: "starter",      name: "Starter",      priceUsd: 29,  cta: "Subscribe",        ctaVariant: "outline" },
  { id: "professional", name: "Professional", priceUsd: 99,  cta: "Subscribe",        ctaVariant: "outline" },
  { id: "pro_radio",    name: "Pro Radio",    priceUsd: 299, cta: "Subscribe",        ctaVariant: "amber",
    badge: { text: "Most popular",           bg: "#f59e0b", color: "#1a0a00", border: "#f59e0b" } },
  { id: "network",      name: "Network",      priceUsd: 499, cta: "Subscribe",        ctaVariant: "outline",
    badge: { text: "For multi-station groups", bg: "#0a2a44", color: "#4a9eff", border: "#1e4d7a" } },
];

// Feature-to-tier map · Feb 2026 spec.
// Rules:
//   • Every feature that ships in an upstream tier is also true downstream.
//     Cells are populated to reflect that.
//   • Values are either `true`/`null` (rendered as ✓ / —) or a string
//     (rendered as amber-tinted metric text — e.g. "50 credits / mo").
const SECTIONS = [
  {
    title: "Usage & Team",
    features: [
      { name: "Stations",              sub: null, values: ["1", "1", "1", "1", "Unlimited"],                 types: ["val", "val", "val", "val", "blue"] },
      { name: "AI credits / month",    sub: null, values: ["50", "300", "1,500", "5,000", "15,000"],         types: ["val", "val", "val", "amber", "blue"] },
      { name: "Team members",          sub: null, values: ["1", "1", "3", "10", "Unlimited"],                types: ["val", "val", "val", "amber", "blue"] },
      { name: "BroadcastFlow watermark on exports", sub: "Removed on any paid plan", values: [true, false, false, false, false], types: ["check", "cross", "cross", "cross", "cross"] },
    ],
  },
  {
    title: "Core Broadcast",
    features: [
      { name: "Control Room & Station settings", sub: null, values: [true, true, true, true, true], types: ["check", "check", "check", "check", "check"] },
      { name: "Content Library",                 sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Now Playing (display)",           sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "WordPress publishing",            sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Basic social posts",              sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Social Scheduler",                sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Now-Playing Auto Poster",         sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
    ],
  },
  {
    title: "Data & News",
    features: [
      { name: "News Ingestion & RSS Feeds",     sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Blog Connector",                 sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Transcribe (capped hrs/mo)",     sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "AI News Center / News Studio",   sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Top Charts (Apple / Last.fm / Deezer)", sub: null, values: [null, null, true, true, true], types: ["none", "none", "check", "check", "check"] },
      { name: "AI News Anchor / Video News Studio", sub: null, values: [null, null, null, true, true], types: ["none", "none", "none", "check", "check"] },
      { name: "Centralized newsroom",           sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
    ],
  },
  {
    title: "Content Generation",
    features: [
      { name: "AI DJ Assistant",                 sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "AI Music Studio (instrumental)",  sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Podcast Studio (Vault + Creator)",sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Quick Promo Creator",             sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Schedule & Academy",              sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "AI Commercial Creator",           sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
    ],
  },
  {
    title: "Studio & Voice (Pro Radio)",
    features: [
      { name: "Radio Imaging Studio",            sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Vision Studio",                   sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Audio Studio",                    sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "AI Avatar Studio",                sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Stem Separation Studio",          sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "AI Vocalist Studio",              sub: "30s cap · BYOK", values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Karaoke Studio",                  sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Production Suite",                sub: "DAW-style multitrack", values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Voice tracking",                  sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Artist Drops",                    sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Push to Air & Program Guide",     sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "AI Providers (BYOK key mgmt)",    sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
    ],
  },
  {
    title: "Analytics & Reporting",
    features: [
      { name: "Basic analytics",         sub: null, values: [null, true, true, true, true], types: ["none",  "check", "check", "check", "check"] },
      { name: "Advanced analytics",      sub: null, values: [null, null, true, true, true], types: ["none",  "none",  "check", "check", "check"] },
      { name: "Advanced reporting",      sub: null, values: [null, null, null, true, true], types: ["none",  "none",  "none",  "check", "check"] },
      { name: "Network analytics",       sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
    ],
  },
  {
    title: "Multi-Station (Network only)",
    features: [
      { name: "Unlimited stations",         sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "Shared content libraries",   sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "Multi-station publishing",   sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "Station brand voice",        sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "Org-wide Integrations & Social Connections", sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "White-label option",         sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
      { name: "24/7 success manager",       sub: null, values: [null, null, null, null, true], types: ["none",  "none",  "none",  "none",  "check"] },
    ],
  },
  {
    title: "Support",
    features: [
      { name: "Community support",         sub: null, values: [true, true, true, true, true],   types: ["check", "check", "check", "check", "check"] },
      { name: "Priority support",          sub: null, values: [null, null, null, true, true],   types: ["none",  "none",  "none",  "check", "check"] },
      { name: "24/7 success manager",      sub: null, values: [null, null, null, null, true],   types: ["none",  "none",  "none",  "none",  "check"] },
    ],
  },
];

const renderCell = (value, type) => {
  if (type === "check") return <span style={{ color: "#3ecf8e", fontSize: "18px" }} aria-label="Included">✓</span>;
  if (type === "cross") return <span style={{ color: "#1e2130", fontSize: "16px" }} aria-hidden="true">—</span>;
  if (type === "none")  return <span style={{ color: "#4a5068" }} aria-label="Not included">—</span>;
  if (type === "val")   return <span style={{ fontSize: "12px", fontWeight: 500, color: "#c8ccd8" }}>{value}</span>;
  if (type === "amber") return <span style={{ fontSize: "12px", fontWeight: 500, color: "#f59e0b" }}>{value}</span>;
  if (type === "blue")  return <span style={{ fontSize: "12px", fontWeight: 500, color: "#4a9eff" }}>{value}</span>;
  return null;
};

const CtaHeader = ({ plan, onCtaClick }) => {
  const isAmber = plan.ctaVariant === "amber";
  const style = isAmber
    ? { background: "#f59e0b", color: "#1a0a00", border: "1px solid #f59e0b" }
    : { background: "transparent", color: "#c8ccd8", border: "0.5px solid #1e2130" };
  return (
    <Link
      to="/login"
      onClick={() => onCtaClick(`compare-header-${plan.id}`)}
      data-testid={`compare-header-cta-${plan.id}`}
      style={{
        display: "inline-block",
        padding: "6px 14px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 500,
        textDecoration: "none",
        marginTop: "10px",
        transition: "opacity 0.15s ease",
        ...style,
      }}
    >
      {plan.cta}
    </Link>
  );
};

export default function CompareTable({ open, trackCta = () => {} }) {
  const { fmt: fx } = useCurrency();
  return (
    <div
      data-testid="compare-plans-wrapper"
      className={open ? "open" : ""}
      style={{
        overflow: "hidden",
        maxHeight: open ? "5000px" : "0px",
        opacity: open ? 1 : 0,
        transition: "max-height 0.4s ease, opacity 0.3s ease",
      }}
    >
      <div style={{ background: "#0f1117", padding: "40px 0 8px", borderTop: "0.5px solid #1e2130", marginTop: "24px" }}>
        {/* Horizontal scroll on mobile */}
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table
            data-testid="compare-plans-table"
            style={{
              width: "100%",
              minWidth: "680px",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            <colgroup>
              <col style={{ width: "28%" }} />
              {PLAN_COLS.map((p) => <col key={p.id} style={{ width: "14.4%" }} />)}
            </colgroup>

            {/* Header row */}
            <thead>
              <tr>
                <th style={{ padding: "20px 16px 16px", textAlign: "left" }}></th>
                {PLAN_COLS.map((plan) => (
                  <th
                    key={plan.id}
                    style={{ padding: "20px 8px 16px", textAlign: "center", verticalAlign: "top" }}
                    data-testid={`compare-header-${plan.id}`}
                  >
                    {plan.badge && (
                      <div
                        className={plan.id === "pro" ? "popular-pulse" : ""}
                        style={{
                          display: "inline-block",
                          padding: "3px 8px",
                          borderRadius: "999px",
                          background: plan.badge.bg,
                          color: plan.badge.color,
                          border: `0.5px solid ${plan.badge.border}`,
                          fontSize: "9px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          marginBottom: "8px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {plan.badge.text}
                      </div>
                    )}
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#e2e4ea" }}>{plan.name}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>{fx(plan.priceUsd)}/mo</div>
                    <CtaHeader plan={plan} onCtaClick={trackCta} />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {SECTIONS.map((section) => (
                <React.Fragment key={section.title}>
                  <tr>
                    <td
                      colSpan={6}
                      data-testid={`compare-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
                      style={{
                        background: "#080b10",
                        color: "#4a5068",
                        fontSize: "10px",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        padding: "12px 16px",
                        borderTop: "0.5px solid #1e2130",
                      }}
                    >
                      {section.title}
                    </td>
                  </tr>
                  {section.features.map((row) => (
                    <tr
                      key={row.name}
                      data-testid={`compare-row-${row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      style={{ borderBottom: "0.5px solid #1e2130" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#0a0d14")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ fontSize: "13px", fontWeight: 500, color: "#c8ccd8" }}>{row.name}</div>
                        {row.sub && (
                          <div style={{ fontSize: "11px", fontWeight: 400, color: "#6b7280", marginTop: "1px" }}>
                            {row.sub}
                          </div>
                        )}
                      </td>
                      {row.values.map((v, i) => (
                        <td key={i} style={{ padding: "12px 8px", textAlign: "center" }}>
                          {renderCell(v, row.types[i])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom CTA row */}
        <div style={{ textAlign: "center", padding: "24px 16px 8px" }} data-testid="compare-bottom-cta">
          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }}>
            Not sure which plan is right for your station?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap" }}>
            <a
              href="mailto:hello@broadcastflow.ai?subject=Which%20plan%20fits%20my%20station%3F"
              data-testid="compare-cta-talk-to-us"
              onClick={() => trackCta("compare-talk-to-us")}
              style={{
                fontSize: "12px",
                padding: "8px 18px",
                borderRadius: "6px",
                border: "0.5px solid #1e2130",
                color: "#c8ccd8",
                background: "#0a0d14",
                cursor: "pointer",
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Talk to us
            </a>
            <Link
              to="/login"
              data-testid="compare-cta-start-pro"
              onClick={() => trackCta("compare-start-pro-radio")}
              style={{
                fontSize: "12px",
                padding: "8px 18px",
                borderRadius: "6px",
                border: "none",
                color: "#1a0a00",
                background: "#f59e0b",
                fontWeight: 500,
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Start with Pro Radio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
